import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { ValidationError } from "@common/validationError";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { EntityTemplateField, LegalEntityStandardField } from "../../entityTemplates/EntityTemplate.types";
import { LegalEntityCustomFieldRenderer, LegalEntityFieldModel, LegalEntityStandardFieldRenderer } from "../../entityTemplates/LegalEntityFieldRenderers";
import LegalEntityCountryField from "../../entityTemplates/fields/legalEntityFields/LegalEntityCountryField.tsx";
import SelectableLegalEntityMultiSelect from "../SelectableLegalEntityMultiSelect";
import { LegalEntitySaveModel } from "../addLegalEntityDialog/AddLegalEntityDialog.types";

export interface LegalEntityTemplateFieldProps {
    field: EntityTemplateField;
    saveModel: LegalEntitySaveModel;
    onNameChange: (name: string) => void;
    onCountryChange: (countryCode: string) => void;
    nameErrorText?: string;
    countryErrorText?: string;
    // The name and country come from the CVR lookup once a company is picked, so the user edits neither.
    disableSaveModelFields: boolean;
    legalEntity: LegalEntityFieldModel;
    onChange: (changeFunc: (legalEntity: LegalEntityFieldModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tagIds: Array<string>) => void;
}

/**
 * One template field, rendered for a legal entity that has not been created yet. Every branch here replaces a
 * standard renderer that either saves on its own or needs a real entity id. The rest go through the standard
 * renderers untouched.
 */
function LegalEntityTemplateField(props: LegalEntityTemplateFieldProps) {
    const { translateString } = useTranslation();
    const standardField = props.field.standardFieldIdentifier as LegalEntityStandardField | undefined;

    const errorTextFor = (field: LegalEntityStandardField) => props.validationErrors.find((v) => v.field === LegalEntityStandardField[field])?.error;

    // The standard name field debounces into the entity; the pane keeps the name on its save model so the CVR
    // lookup can fill it and validation can run before anything else
    if (standardField === LegalEntityStandardField.LegalEntityName) {
        return (
            <DotLegalTextField
                debounce={false}
                size="small"
                label={translateString("name")}
                value={props.saveModel.name}
                onChange={props.onNameChange}
                errorText={props.nameErrorText}
                autoFocus
                disabled={props.disableSaveModelFields}
            />
        );
    }

    if (standardField === LegalEntityStandardField.LegalEntityCountry) {
        return (
            <LegalEntityCountryField
                country={props.saveModel.countryCode === "" ? undefined : props.saveModel.countryCode}
                onChange={props.onCountryChange}
                disabled={props.disableSaveModelFields}
                isLoading={false}
                errorText={props.countryErrorText}
            />
        );
    }

    if (standardField === LegalEntityStandardField.LegalEntityTags) {
        return (
            <TagsMultiSelect
                selectedTags={props.selectedTagIds}
                onChange={props.onTagsChange}
                entityType={TagRelationType.LegalEntity}
                isLoading={false}
                errorText={errorTextFor(LegalEntityStandardField.LegalEntityTags)}
            />
        );
    }

    // Sub contractors persist against an entity id in the normal renderer, so creation collects them with a
    // plain selector instead.
    if (standardField === LegalEntityStandardField.LegalEntitySubContractors) {
        return (
            <SelectableLegalEntityMultiSelect
                selectedIds={props.legalEntity.subContractorIds}
                onChange={(subContractorIds) => props.onChange((legalEntity) => (legalEntity.subContractorIds = subContractorIds))}
                label={translateString("subContractors")}
                errorText={errorTextFor(LegalEntityStandardField.LegalEntitySubContractors)}
            />
        );
    }

    if (standardField) {
        return (
            <LegalEntityStandardFieldRenderer
                field={props.field}
                legalEntity={props.legalEntity}
                legalEntityId=""
                onChange={props.onChange}
                validationErrors={props.validationErrors}
                isLoading={false}
                isPreview={false}
            />
        );
    }

    return (
        <LegalEntityCustomFieldRenderer
            field={props.field}
            legalEntity={props.legalEntity}
            legalEntityId=""
            onChange={props.onChange}
            validationErrors={props.validationErrors}
            isLoading={false}
            isPreview={false}
        />
    );
}

export default LegalEntityTemplateField;
